import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { ColetaAoVivo, ColetaEstado, ProtegidaMenu } from '../../api/types';
import { ApiError } from '../../api/types';
import { iconePara } from '../../assets/items';
import { numero, t, umaCasa } from '../../i18n/idioma';
import { RECURSOS, recursoDe, type Recurso } from './coletaRecurso';
import { ImgDoItem } from '../ItemIcon';
import { nomeDoItem } from '../../i18n/nomes';
import type { ViagemDestino } from '../../world/viagens';
import { GumpWindow, type WindowAnchor } from '../GumpWindow';
import { IcCasa, IcCidade, IcGlobo, IcMonstro } from '../icones/IconesV3';

/**
 * Teleportar (techspec-teleportar; era o Viajar da techspec-hud-v3 §05). Desde a techspec-hud-v3 §18 são dois botões
 * flutuantes: Teleportar abre Cidades e Locais; Recursos abre só a lista de coleta, sem abas (`recursos`).
 * Cidades são as cidades principais (Minoc e Cove); Locais é todo o resto que o doc de viagens lista (Britain, os
 * cemitérios, as dungeons…), e só teleporta: caçar continua pelo botão Caçar da barra. Recursos: Minério e Madeira
 * abrem o cartão da coleta (techspec-coleta §07, techspec-mineracao §07). O `tipo` de cada destino vem do servidor.
 * Coletando, o destino vira "Recall · encerra a coleta"; caçando, tudo tranca. Mapa sem guarda (sem proteção contra PK)
 * leva uma caveira discreta; o servidor diz quem tem guarda.
 */
type Aba = 'cidades' | 'locais';
const ABAS: ReadonlyArray<[Aba, string, typeof IcCidade]> = [
  ['cidades', 'hud.viajar.cidades', IcCidade],
  ['locais', 'hud.viajar.locais', IcGlobo]
];

/** O escudo da área protegida (techspec-cacada-protegida). */
function IcEscudo({ className, title }: { className?: string; title?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" role={title ? 'img' : undefined} aria-hidden={title ? undefined : true}>
      {title && <title>{title}</title>}
      <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z" />
    </svg>
  );
}

export function ViajarGump({
  anchor, recursos = false, mapaId, destinos, cacando, coleta, coletaEstado, onFechar, onDestino, onColetaIniciada, onColetaReabrir
}: {
  anchor: WindowAnchor;
  /** O botão Recursos: a lista de Minério e Madeira, sem as abas de destino. */
  recursos?: boolean;
  mapaId: string | undefined;
  destinos: readonly ViagemDestino[];
  cacando: boolean;
  /** A coleta aberta (o gump ao vivo), ou null. */
  coleta: ColetaAoVivo | null;
  coletaEstado: ColetaEstado | null;
  onFechar(): void;
  /** Coletando, o destino encerra a coleta por Recall (quem chama decide). `protegida`: a chave da aba Locais. */
  onDestino(d: ViagemDestino, protegida: boolean): void;
  onColetaIniciada(estado: ColetaEstado): void;
  /** A linha "você está aqui" abre de novo a janela da coleta minimizada. */
  onColetaReabrir?(): void;
}) {
  const coletando = !!coleta;
  const [aba, setAba] = useState<Aba>('cidades');
  // Um cartão aberto por vez; o minério (o novo) abre primeiro. Coletando, nenhum.
  const [cartao, setCartao] = useState<string | null>(coletando ? null : 'minerio');
  const lista = destinos.filter(d => (aba === 'cidades') === (d.tipo === 'cidade'));
  const Icone = aba === 'cidades' ? IcCidade : IcGlobo;
  // A chave "Área PvP / Área protegida" (techspec-cacada-protegida §02.1): abre sempre em PvP, só na aba Locais.
  const [pvp, setPvp] = useState(true);
  const [protegida, setProtegida] = useState<ProtegidaMenu | null>(null);
  useEffect(() => {
    if (recursos) return;
    let vivo = true;
    void api.protegidaMenu().then(p => { if (vivo) setProtegida(p); }).catch(() => {});
    return () => { vivo = false; };
  }, [recursos]);
  const temChave = aba === 'locais' && !!protegida?.ativa;
  const naProtegida = temChave && !pvp;
  const semArea = (d: ViagemDestino) => !!protegida?.mapasSem.includes(d.mapa);

  if (recursos) {
    return (
      <GumpWindow
        title={t('hud.viajar.recursos')}
        anchor={anchor}
        className="pop vj win--viajar win--recursos"
        posicao="recursos"
        onClose={onFechar}
      >
        <div className="vj-lista">
          {RECURSOS.map(r => (
            <LinhaColeta key={r.id} rec={r} coleta={coleta} estado={coletaEstado} cacando={cacando} onIniciada={onColetaIniciada}
              aberta={cartao === r.id} onAbrir={() => setCartao(c => (c === r.id ? null : r.id))} onReabrir={onColetaReabrir} />
          ))}
          <p className="vj-nota">{t('hud.viajar.recursosNota')}</p>
        </div>
      </GumpWindow>
    );
  }

  return (
    <GumpWindow
      title={t('hud.menu.viajar')}
      anchor={anchor}
      className="pop vj win--viajar"
      posicao="viajar"
      onClose={onFechar}
    >
      <div className="vj-abas" role="tablist" aria-label={t('hud.menu.viajar')}>
        {ABAS.map(([id, nome, Ic]) => (
          <button key={id} type="button" role="tab" aria-selected={aba === id} aria-pressed={aba === id} onClick={() => setAba(id)}>
            <Ic />{t(nome)}
          </button>
        ))}
      </div>
      {temChave && (
        <button type="button" role="switch" aria-checked={pvp} className={`vj-area${pvp ? '' : ' is-prot'}`} onClick={() => setPvp(v => !v)}>
          <span className="vj-area__t">
            <b>{t(pvp ? 'hud.viajar.areaPvp' : 'hud.viajar.areaProtegida')}</b>
            <small>{pvp ? t('hud.viajar.areaPvpDica') : t('hud.viajar.areaProtegidaDica', { xp: protegida!.xp, saque: protegida!.saque })}</small>
          </span>
          <span className="vj-area__s" aria-hidden="true"><i /></span>
        </button>
      )}
      <div className="vj-lista" role="tabpanel">
          <>
            {(cacando || coletando) && (
              <p className="vj-nota is-aviso">{t(cacando ? 'hud.viajar.pareCacada' : 'hud.viajar.encerraColeta')}</p>
            )}
            <div className="vj-dest">
              {lista.map(d => {
                // O mapa atual também abre: teleportar para ele troca de canal (o do grupo, ou um sorteado entre os
                // que têm gente) — techspec-canais-lotes-e-saque-dividido §03. Sem outro canal, só reposiciona.
                const aqui = !d.casa && !coletando && !!mapaId && d.mapa === mapaId;
                // Na área protegida, o local sem ela fica travado (o servidor recusa do mesmo jeito).
                const off = cacando || (naProtegida && semArea(d));
                return (
                  <button
                    key={d.id}
                    type="button"
                    className={`vj-d${aqui ? ' is-aqui' : ''}`}
                    aria-disabled={off || undefined}
                    title={[t(d.rotulo), aqui ? `${t('hud.viajar.aqui')} · ${t('hud.viajar.trocarCanal')}` : '', d.semGuarda ? t('hud.viajar.semGuarda') : ''].filter(Boolean).join(' · ')}
                    onClick={off ? undefined : () => onDestino(d, naProtegida)}
                  >
                    {d.casa ? <IcCasa /> : <Icone />}
                    <b>{t(d.rotulo)}</b>
                    <span className="vj-d__m">
                      {d.semGuarda && !naProtegida && <IcMonstro className="vj-pk" title={t('hud.viajar.semGuarda')} />}
                      {naProtegida && !semArea(d) && <IcEscudo className="vj-esc" title={t('hud.viajar.areaProtegida')} />}
                      {aqui && <i aria-label={t('hud.viajar.aqui')}>●</i>}
                    </span>
                  </button>
                );
              })}
            </div>
            {lista.length === 0 && <p className="vj-nota">{t('hud.viajar.semPortal')}</p>}
            {lista.some(d => d.semGuarda) && !naProtegida && (
              <p className="vj-nota vj-legenda"><IcMonstro className="vj-pk" />{t('hud.viajar.semGuardaLegenda')}</p>
            )}
          </>
      </div>
    </GumpWindow>
  );
}

/**
 * Teleportar → Recursos → Minério ou Madeira: o cartão que confirma antes do Recall (techspec-coleta §07).
 * O minério abre primeiro (é o novo); com uma coleta aberta, a linha dela diz "você está aqui" e a
 * outra fica trancada.
 */
function LinhaColeta({ rec, coleta, estado, cacando, onIniciada, aberta, onAbrir, onReabrir }: {
  rec: Recurso;
  coleta: ColetaAoVivo | null;
  estado: ColetaEstado | null;
  cacando: boolean;
  onIniciada(e: ColetaEstado): void;
  aberta: boolean;
  onAbrir(): void;
  onReabrir?(): void;
}) {
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const lugar = estado?.lugares.find(l => l.id === rec.id);
  const daColeta = !!coleta && recursoDe(coleta.recurso).id === rec.id;
  const outraColeta = !!coleta && !daColeta;
  const aqui = daColeta;
  const skill = daColeta ? coleta!.skill : lugar?.skill ?? 0;
  const ferramenta = lugar?.ferramenta ?? null;
  const bonus = lugar?.bonus ?? 0;
  const tem = !!ferramenta;
  const coletores = lugar ? Math.max(0, lugar.lotacao - lugar.vagas) : null;
  const pode = !!lugar && tem && !coleta && !cacando && !busy;

  async function ir() {
    if (!pode) return;
    setBusy(true);
    setErro(null);
    try {
      onIniciada(await api.iniciarColeta(rec.id));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : t('coleta.erroIr'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className={`vj-l is-coleta is-${rec.id}`} aria-expanded={aberta && !coleta}
        aria-disabled={outraColeta || undefined} onClick={outraColeta ? undefined : aqui ? onReabrir : onAbrir}>
        <span className="vj-ic"><img src={iconePara(rec.itemComum, 25)} alt="" /></span>
        <span className="vj-n">
          <b>{t(rec.t.linha)}</b>
          <small>{aqui
            ? (coleta!.arvore ? t(rec.t.vjAquiPonto, { n: coleta!.arvore }) : t('hud.viajar.aqui'))
            : outraColeta
              ? t('hud.viajar.outraColeta')
              : t(rec.t.vjLugar, { lugar: lugar?.nome ?? t(rec.t.lugar), skill: umaCasa(skill) })}</small>
        </span>
        {coletores !== null && <span className="cl-vg" title={t(rec.t.coletores)}>{coletores}/{lugar!.lotacao}</span>}
        <i>{aqui ? '●' : outraColeta ? '—' : aberta ? '▾' : '›'}</i>
      </button>
      {aberta && !coleta && (
        <div className="cl-ida">
          <div className="cl-req">
            <div className={tem ? 'is-ok' : 'is-nao'}>
              <ImgDoItem id={tem ? ferramenta! : rec.ferramentaPadrao} size={18} />
              <span>{tem
                ? t(bonus > 0 ? rec.t.temFerramentaBonus : 'coleta.temMachadinha',
                  { nome: nomeDoItem(ferramenta!), n: numero(bonus) })
                : t(rec.t.semFerramenta)}</span>
              <i>{tem ? '✓' : '✕'}</i>
            </div>
            {cacando && <div className="is-nao"><span>{t('hud.viajar.pareCacada')}</span><i>✕</i></div>}
          </div>
          {erro && <p className="cl-aviso" role="alert">{erro}</p>}
          <button type="button" className="btn btn--primary hunt-cta" disabled={!pode} onClick={() => void ir()}>
            {busy ? t('comum.carregando') : t('coleta.ir')}
          </button>
        </div>
      )}
    </>
  );
}
